import AjaxUtils from '../utils/ajax-utils';
import DeviceManager, { onPromiseAttrType } from '../utils/device-manager';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare const app: any;
/* eslint-enable @typescript-eslint/no-explicit-any */

type AutocompletePost = { id: number|string, url: string, title: string };

export default function initArchiveFilters() : void {
    const $filtersContainers = document.querySelectorAll<HTMLElement>('.js-archive-filters');

    $filtersContainers.forEach($container => {
        new ArchiveFilters($container);
    });
}

class ArchiveFilters {
    $container: HTMLElement;
    $categoryLinks: HTMLAnchorElement[]|null;
    $categorySelect: HTMLSelectElement|null;
    $searchInput: HTMLInputElement|null;
    $searchSubmitButton: HTMLButtonElement|null;
    $autocomplete: HTMLElement|null;
    $autocompleteLoader: HTMLElement;
    autocompleteLastSearch: string|null;
    autocompleteController: AbortController|null;
    autocompleteTimeout: ReturnType<typeof setTimeout>;
    autocompleteCache: {[key: string]: Array<AutocompletePost>} = {};

    constructor($container: HTMLElement) {
        this.$container = $container;
        this.$categoryLinks = Array.from(this.$container.querySelectorAll('.js-category')) || null;
        this.$categorySelect = this.$container.querySelector('.js-category-select') || null;
        this.$searchInput = this.$container.querySelector('.js-search') || null;
        this.$searchSubmitButton = this.$container.querySelector('.js-search-submit') || null;

        // If category links (for desktop) && category select (for mobile) exists
        if (this.$categoryLinks && this.$categorySelect) {
            this.$categorySelect.addEventListener('change', () => {
                const $categoryLink = this.$categoryLinks.filter($link => $link.dataset.category === this.$categorySelect.value)[0] ?? null;

                if ($categoryLink) {
                    $categoryLink.click();
                }
            });
        }

        // If the search input exists
        if (this.$searchInput) {
            // Initialize the autocomplete area
            DeviceManager.addCallback((attr: onPromiseAttrType) => {
                if (!this.$autocomplete && DeviceManager.isGreaterThanOrEqual(attr.current, 'md')) {
                    this.initAutocomplete();
                }
            });
        }
    }

    /**
     * Reload the page with right filters
     * 
     * @return {void}
     */
    applyFilters = () : void => {
        const searchParams = new URLSearchParams(window.location.search);

        if (this.$searchInput) {
            // Set the search parameter
            if (this.$searchInput.value) {
                searchParams.set('search', this.$searchInput.value);
            } else {
                searchParams.delete('search');
            }
        }

        // Construct the URL and redirect
        let url = (window.location.origin + window.location.pathname).replace(/\/\d+\/$/, '').replace(/\/page/, '');
        if (searchParams.toString().length > 0) {
            url += '?';
        }
        url += searchParams.toString();
        window.location.href = url;
    }

    /**
     * Initialize the autocomplete area
     * 
     * @return {void}
     */
    initAutocomplete() : void {
        this.$autocomplete = document.createElement('ul');
        this.$autocomplete.classList.add('autocomplete', 'js-autocomplete', 'hidden');

        this.$autocompleteLoader = document.createElement('div');
        this.$autocompleteLoader.append(document.createElement('div'));
        this.$autocompleteLoader.append(document.createElement('div'));
        this.$autocompleteLoader.append(document.createElement('div'));
        this.$autocompleteLoader.classList.add('autocomplete-loader', 'hidden');

        this.$searchInput.parentElement.append(this.$autocomplete);
        this.$searchInput.parentElement.append(this.$autocompleteLoader);

        // Perform a search request when search input value change
        this.$searchInput.addEventListener('keyup', () => {
            const searchValue = this.$searchInput.value.trim();

            if (searchValue.length > 2) {
                if (searchValue !== this.autocompleteLastSearch) {
                    this.autocompleteLastSearch = searchValue;

                    this.searchAutocomplete();
                }
            } else {
                this.autocompleteLastSearch = '';
                this.$autocomplete.innerHTML = '';
                this.closeAutocomplete();
            }
        });

        // Close the autocomplete area when clicking outside
        document.addEventListener('click', (evt: MouseEvent) => {
            if (
                this.$autocomplete.classList.contains('hidden') || // The autocomplete area is already closed
                (evt.target as HTMLElement).closest('.js-autocomplete') || // The clicked element is the autocomplete area
                evt.target === this.$searchInput // The clicked element is the search input
            ) {
                return;
            }

            this.closeAutocomplete();
        });

        // Close the autocomplete area when the activeElement change
        document.addEventListener('keyup', (evt: KeyboardEvent) => {
            if (
                this.$autocomplete.classList.contains('hidden') || // The autocomplete area is already closed
                (evt.key !== 'Tab') || // The pressed key is not "Tab"
                document.activeElement.closest('.js-autocomplete') || // The active element is the autocomplete area
                document.activeElement === this.$searchInput // The active element is the search input
            ) {
                return;
            }

            this.closeAutocomplete();
        });

        // Re-open the autocomplete area on focus on the search input
        this.$searchInput.addEventListener('focus', () => {
            if (this.$autocomplete.childElementCount < 1 && this.$searchInput.value.trim().length > 2) {
                this.searchAutocomplete();
            }

            if (!this.$autocomplete.classList.contains('hidden') || this.$autocomplete.childElementCount < 1) {
                return;
            }

            this.openAutocomplete();
        });
    }

    /**
     * Perform a search request and populate the autocomplete area
     * 
     * @return {void}
     */
    searchAutocomplete() : void {
        if (this.autocompleteController) {
            this.autocompleteController.abort();
        }

        const searchValue = this.$searchInput.value.trim();

        // Clear the timeout if exists
        if (this.autocompleteTimeout) {
            clearTimeout(this.autocompleteTimeout);
        }

        // Hide and clear the autocomplete area
        this.$autocomplete.classList.add('hidden');
        this.$autocomplete.innerHTML = '';

        // Show the loader
        this.$autocompleteLoader.classList.remove('hidden');

        // Get posts from cache
        if (this.autocompleteCache[searchValue]) {
            this.fillAutocomplete(this.autocompleteCache[searchValue]);

            // Hide the loader
            this.$autocompleteLoader.classList.add('hidden');

            return;
        }

        // Wait 500ms before searching
        this.autocompleteTimeout = setTimeout(() => {
            // Get posts from ajax if no cache exists
            this.autocompleteController = new AbortController();
            AjaxUtils.postData(app.ajaxUrl, {
                action: 'search_in_post_type',
                search: searchValue,
                post_type: this.$searchInput.dataset.postType,
            }, this.autocompleteController.signal).then(response => {
                this.fillAutocomplete(response.data.posts);

                if (response.data.posts.length > 0) {
                    this.autocompleteController = null;
                }

                // Cache results
                this.autocompleteCache[searchValue] = response.data.posts;

                // Hide the loader
                this.$autocompleteLoader.classList.add('hidden');
            }).catch(() => {
                this.autocompleteController = null;

                // Hide the loader
                this.$autocompleteLoader.classList.add('hidden');
            });
        }, 500);
    }

    /**
     * Fill the autocomplete area with posts
     * 
     * @param {Array<AutocompletePost>} posts
     * 
     * @return {void}
     */
    fillAutocomplete(posts: Array<AutocompletePost>) : void {
        const $lines = [];

        posts.forEach((post: AutocompletePost) => {
            const $li = document.createElement('li');
            const $a = document.createElement('a');
            $a.href = post.url;
            $a.title = post.title;
            $a.innerHTML = post.title;

            $li.append($a);
            $lines.push($li);
        });

        this.$autocomplete.append(...$lines);

        if (posts.length > 0) {
            this.$autocomplete.classList.remove('hidden');
        }
    }

    /**
     * Close the autocomplete area
     * 
     * @return {void}
     */
    closeAutocomplete() : void {
        this.$autocomplete.classList.add('hidden');
    }

    /**
     * Open the autocomplete area
     * 
     * @return {void}
     */
    openAutocomplete() : void {
        this.$autocomplete.classList.remove('hidden');
    }
}
